import { type Common } from './common.js';
import {
  type Connection,
  type ConnectionApi,
  type ConnectionIntegrationEngine,
  type ConnectionInterfaceEngine,
  type ConnectionResource,
  type ConnectionSftp,
  type CreateInterfaceEngineConnectorValues,
  type FindAllOptions,
  type FindInterfaceEngineEventsOptions,
  type GetConnectionResourcesOptions,
  type GetFileSignedUrlOptions,
  type InterfaceEngineChannel,
  type InterfaceEngineEventsResponse,
  type SignedUrlResponse,
} from './models/connection.js';

export class API {
  common: Common;
  baseEndpoint = '/connections';

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options: FindAllOptions): Promise<{ connections: Connection[] }> {
    const url = this.common.constructPath(this.baseEndpoint, options);
    return await this.common.getJSON(url);
  }

  async findOneById(args: {
    /**
     * FIXME: Remove detailPath as a parameter once we have one endpoint that handles all connection types.
     */
    detailPath: string;
  }): Promise<{
    connection:
      | ConnectionApi
      | ConnectionSftp
      | ConnectionInterfaceEngine
      | ConnectionIntegrationEngine;
  }> {
    const { detailPath } = args;
    const url = this.common.constructPath(detailPath);
    return await this.common.getJSON(url);
  }

  async findEvents(
    options: FindInterfaceEngineEventsOptions
  ): Promise<InterfaceEngineEventsResponse> {
    const { detailPath, ...rest } = options;
    const url = this.common.constructPath(detailPath, rest);
    return await this.common.getJSON(url);
  }

  async createInterfaceEngineConnector(
    values: CreateInterfaceEngineConnectorValues
  ): Promise<InterfaceEngineChannel> {
    const url = this.common.constructPath(`${this.baseEndpoint}/interface-engine/sftp`);
    return await this.common.postJSON(url, values);
  }

  /**
   * Get resource types for a connection (via resource-gateway proxy)
   */
  async getConnectionResourceTypes(args: {
    connectionId: string;
  }): Promise<{ resourceTypes: { resourceKey: string; name: string }[] }> {
    const { connectionId } = args;
    const url = this.common.constructPath(
      `/resource-gateway/connections/${connectionId}/resource-types`
    );
    return await this.common.getJSON(url);
  }

  /**
   * Get resources for a connection (via resource-gateway proxy)
   */
  async getConnectionResources(options: GetConnectionResourcesOptions): Promise<{
    resources: ConnectionResource[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const { connectionId, resourceKey, limit = 25, offset = 0 } = options;
    const url = this.common.constructPath(
      `/resource-gateway/connections/${connectionId}/resources`,
      { resourceKey, limit, offset }
    );
    return await this.common.getJSON(url);
  }

  /**
   * Get a signed URL for previewing/downloading a file (via resource-gateway proxy)
   */
  async getFileSignedUrl(options: GetFileSignedUrlOptions): Promise<SignedUrlResponse> {
    const { fileId, organizationId, disposition = 'inline' } = options;
    const url = this.common.constructPath(
      `/resource-gateway/resource-access/files/${fileId}/signed-url`,
      { organizationId, disposition }
    );
    return await this.common.getJSON(url);
  }
}
